"use client";

import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { PiLockBold } from "react-icons/pi";

import PaywallTracker from "@/components/analytics/paywall-tracker";
import { Button } from "@/components/button/button";
import Input from "@/components/input/input";
import { PAGES } from "@/configs/pages.config";
import {
  classifyPreviewFailureReason,
  getSourceFromUrl,
  trackAnalyticsApiEvent,
  trackLinkPasted,
  trackPremiumFeatureClicked,
} from "@/lib/analytics";
import { buildEndpointCandidates, fetchWithFallback } from "@/lib/fetch-with-fallback";
import { DEFAULT_LOCALE, getLocaleFromPathname, SupportedLocale } from "@/lib/i18n/locales";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { getProxiedImageSrc } from "@/lib/image-proxy";
import { isValidSocialUrl, normalizeExternalUrl } from "@/lib/social-url";

type AnalyticsHookPreview = {
  hook_score: number | null;
  hook_strategy: string | null;
  hook_text: string | null;
  why_it_works: string | null;
  is_preview: boolean;
};

type CommentInsights = {
  themes: string[];
  sentiment: string;
  top_questions: string[];
  sample_comments: string[];
};

type AnalyticsPreviewResponse =
  | {
      status: "ok";
      video: {
        id: string;
        title?: string;
        caption?: string;
        duration_sec: number;
        thumbnail_url: string;
        author: {
          username: string;
          display_name: string;
        };
        view_count?: number;
        like_count?: number;
      };
      preview: {
        teaser_summary?: string;
      };
      analytics: AnalyticsHookPreview;
      comment_insights?: CommentInsights;
      creative_brief?: string;
    }
  | {
      status: "error";
      message: string;
      reason?: string;
    };

type LocaleCopy = {
  title: string;
  subtitle: string;
  analyzeButton: string;
  analyzingButton: string;
  rateLimitButton: string;
  placeholder: string;
  thumbnailAlt: string;
  viewsLabel: string;
  likesLabel: string;
  hookScoreLabel: string;
  hookStrategyLabel: string;
  hookTextLabel: string;
  whyItWorksLabel: string;
  lockedInsightsTitle: string;
  lockedInsightsHint: string;
  lockedBriefTitle: string;
  lockedBriefHint: string;
  lockedUnlock: string;
  lockedHint: string;
  cta: string;
  signUpToAnalyzeButton: string;
  themesLabel: string;
  topQuestionsLabel: string;
  sampleCommentsLabel: string;
  sentimentLabel: string;
  creativeBriefLabel: string;
  demoLabel: string;
  errorInvalidUrl: string;
  errorMissingApi: string;
  errorRateLimit: string;
  errorNetwork: string;
};

const localeCopy: Record<SupportedLocale, LocaleCopy> = {
  en: {
    title: "Paste a competitor Reel to see the hook analysis",
    subtitle: "We'll score the hook and show you a preview of the full analysis.",
    analyzeButton: "Analyze Hook",
    analyzingButton: "Analyzing...",
    rateLimitButton: "Sign up to continue",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Instagram Reel thumbnail",
    viewsLabel: "views",
    likesLabel: "likes",
    hookScoreLabel: "Hook Score",
    hookStrategyLabel: "Strategy",
    hookTextLabel: "Hook (preview)",
    whyItWorksLabel: "Why it works",
    lockedInsightsTitle: "Comment Insights",
    lockedInsightsHint: "Top questions, sentiment, and themes from comments.",
    lockedBriefTitle: "Creative Brief",
    lockedBriefHint: "Full AI-generated brief tailored to your brand bible.",
    lockedUnlock: "Sign up free to unlock",
    lockedHint: "Create a free account to unlock comment insights and creative brief.",
    cta: "Unlock Full Analysis",
    signUpToAnalyzeButton: "Sign up to analyze →",
    themesLabel: "Themes",
    topQuestionsLabel: "Top Questions",
    sampleCommentsLabel: "Comments",
    sentimentLabel: "Sentiment",
    creativeBriefLabel: "Creative Brief",
    demoLabel: "Example — replace the URL above with your own Reel to analyze it",
    errorInvalidUrl: "Please enter a valid Instagram Reel link.",
    errorMissingApi: "Missing API endpoint configuration. Please try again later.",
    errorRateLimit: "Rate limit exceeded. Sign up to continue.",
    errorNetwork: "We couldn't reach Instagram. Please try again.",
  },
  es: {
    title: "Pega un Reel de la competencia para ver el análisis del hook",
    subtitle: "Puntuaremos el hook y mostraremos una vista previa del análisis completo.",
    analyzeButton: "Analizar Hook",
    analyzingButton: "Analizando...",
    rateLimitButton: "Regístrate para continuar",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Miniatura del Reel de Instagram",
    viewsLabel: "vistas",
    likesLabel: "me gusta",
    hookScoreLabel: "Puntuación del Hook",
    hookStrategyLabel: "Estrategia",
    hookTextLabel: "Hook (vista previa)",
    whyItWorksLabel: "Por qué funciona",
    lockedInsightsTitle: "Insights de comentarios",
    lockedInsightsHint: "Principales preguntas, sentimiento y temas de los comentarios.",
    lockedBriefTitle: "Brief Creativo",
    lockedBriefHint: "Brief completo generado por IA adaptado a tu bible de marca.",
    lockedUnlock: "Regístrate gratis para desbloquear",
    lockedHint: "Crea una cuenta gratis para desbloquear los insights de comentarios y el brief creativo.",
    cta: "Desbloquear análisis completo",
    signUpToAnalyzeButton: "Regístrate para analizar →",
    themesLabel: "Temas",
    topQuestionsLabel: "Preguntas principales",
    sampleCommentsLabel: "Comentarios",
    sentimentLabel: "Sentimiento",
    creativeBriefLabel: "Brief Creativo",
    demoLabel: "Análisis de ejemplo — pega una URL de Reel arriba para analizar el tuyo",
    errorInvalidUrl: "Introduce un enlace válido de Reel de Instagram.",
    errorMissingApi: "Falta la configuración del endpoint de API. Inténtalo de nuevo más tarde.",
    errorRateLimit: "Se superó el límite. Regístrate para continuar.",
    errorNetwork: "No pudimos conectar con Instagram. Inténtalo de nuevo.",
  },
  fr: {
    title: "Collez un Reel concurrent pour voir l'analyse du hook",
    subtitle: "Nous scorerons le hook et afficherons un aperçu de l'analyse complète.",
    analyzeButton: "Analyser le Hook",
    analyzingButton: "Analyse en cours...",
    rateLimitButton: "Inscrivez-vous pour continuer",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Miniature du Reel Instagram",
    viewsLabel: "vues",
    likesLabel: "j'aime",
    hookScoreLabel: "Score du Hook",
    hookStrategyLabel: "Stratégie",
    hookTextLabel: "Hook (aperçu)",
    whyItWorksLabel: "Pourquoi ça marche",
    lockedInsightsTitle: "Insights des commentaires",
    lockedInsightsHint: "Questions principales, sentiment et thèmes des commentaires.",
    lockedBriefTitle: "Brief Créatif",
    lockedBriefHint: "Brief complet généré par l'IA, adapté à votre bible de marque.",
    lockedUnlock: "Inscrivez-vous gratuitement pour débloquer",
    lockedHint: "Créez un compte gratuit pour débloquer les insights et le brief créatif.",
    cta: "Débloquer l'analyse complète",
    signUpToAnalyzeButton: "S'inscrire pour analyser →",
    themesLabel: "Thèmes",
    topQuestionsLabel: "Questions principales",
    sampleCommentsLabel: "Commentaires",
    sentimentLabel: "Sentiment",
    creativeBriefLabel: "Brief Créatif",
    demoLabel: "Analyse exemple — collez une URL de Reel ci-dessus pour analyser le vôtre",
    errorInvalidUrl: "Veuillez saisir un lien Reel Instagram valide.",
    errorMissingApi: "Configuration de l'endpoint API manquante. Veuillez réessayer plus tard.",
    errorRateLimit: "Limite dépassée. Inscrivez-vous pour continuer.",
    errorNetwork: "Impossible de joindre Instagram. Veuillez réessayer.",
  },
  de: {
    title: "Füge einen Wettbewerber-Reel ein, um die Hook-Analyse zu sehen",
    subtitle: "Wir bewerten den Hook und zeigen eine Vorschau der vollständigen Analyse.",
    analyzeButton: "Hook analysieren",
    analyzingButton: "Analyse läuft...",
    rateLimitButton: "Registriere dich, um fortzufahren",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Instagram-Reel-Vorschaubild",
    viewsLabel: "Aufrufe",
    likesLabel: "Likes",
    hookScoreLabel: "Hook-Score",
    hookStrategyLabel: "Strategie",
    hookTextLabel: "Hook (Vorschau)",
    whyItWorksLabel: "Warum es funktioniert",
    lockedInsightsTitle: "Kommentar-Insights",
    lockedInsightsHint: "Wichtigste Fragen, Sentiment und Themen aus den Kommentaren.",
    lockedBriefTitle: "Creative Brief",
    lockedBriefHint: "Vollständiges KI-generiertes Brief, angepasst an dein Brand-Bible.",
    lockedUnlock: "Kostenlos registrieren zum Freischalten",
    lockedHint: "Erstelle ein kostenloses Konto, um Kommentar-Insights und Creative Brief freizuschalten.",
    cta: "Vollständige Analyse freischalten",
    signUpToAnalyzeButton: "Registrieren zum Analysieren →",
    themesLabel: "Themen",
    topQuestionsLabel: "Top-Fragen",
    sampleCommentsLabel: "Kommentare",
    sentimentLabel: "Sentiment",
    creativeBriefLabel: "Creative Brief",
    demoLabel: "Beispielanalyse — füge oben eine Reel-URL ein, um deine eigene zu analysieren",
    errorInvalidUrl: "Bitte gib einen gueltigen Instagram-Reel-Link ein.",
    errorMissingApi: "API-Endpunkt-Konfiguration fehlt. Bitte versuche es später erneut.",
    errorRateLimit: "Limit erreicht. Registriere dich, um fortzufahren.",
    errorNetwork: "Instagram konnte nicht erreicht werden. Bitte versuche es erneut.",
  },
  it: {
    title: "Incolla un Reel della concorrenza per vedere l'analisi dell'hook",
    subtitle: "Valuteremo l'hook e mostreremo un'anteprima dell'analisi completa.",
    analyzeButton: "Analizza Hook",
    analyzingButton: "Analisi in corso...",
    rateLimitButton: "Registrati per continuare",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Miniatura del Reel Instagram",
    viewsLabel: "visualizzazioni",
    likesLabel: "mi piace",
    hookScoreLabel: "Punteggio Hook",
    hookStrategyLabel: "Strategia",
    hookTextLabel: "Hook (anteprima)",
    whyItWorksLabel: "Perché funziona",
    lockedInsightsTitle: "Insight dei commenti",
    lockedInsightsHint: "Principali domande, sentiment e temi dai commenti.",
    lockedBriefTitle: "Brief Creativo",
    lockedBriefHint: "Brief completo generato dall'IA, adattato alla tua bible del brand.",
    lockedUnlock: "Registrati gratis per sbloccare",
    lockedHint: "Crea un account gratuito per sbloccare gli insight dei commenti e il brief creativo.",
    cta: "Sblocca analisi completa",
    signUpToAnalyzeButton: "Registrati per analizzare →",
    themesLabel: "Temi",
    topQuestionsLabel: "Domande principali",
    sampleCommentsLabel: "Commenti",
    sentimentLabel: "Sentiment",
    creativeBriefLabel: "Brief Creativo",
    demoLabel: "Analisi di esempio — incolla un URL Reel sopra per analizzare il tuo",
    errorInvalidUrl: "Inserisci un link valido di Reel Instagram.",
    errorMissingApi: "Configurazione dell'endpoint API mancante. Riprova più tardi.",
    errorRateLimit: "Limite raggiunto. Registrati per continuare.",
    errorNetwork: "Non siamo riusciti a raggiungere Instagram. Riprova.",
  },
  pt: {
    title: "Cole um Reel de concorrente para ver a análise do hook",
    subtitle: "Vamos pontuar o hook e mostrar uma prévia da análise completa.",
    analyzeButton: "Analisar Hook",
    analyzingButton: "Analisando...",
    rateLimitButton: "Cadastre-se para continuar",
    placeholder: "https://www.instagram.com/reel/...",
    thumbnailAlt: "Miniatura do Reel do Instagram",
    viewsLabel: "visualizações",
    likesLabel: "curtidas",
    hookScoreLabel: "Pontuação do Hook",
    hookStrategyLabel: "Estratégia",
    hookTextLabel: "Hook (prévia)",
    whyItWorksLabel: "Por que funciona",
    lockedInsightsTitle: "Insights dos comentários",
    lockedInsightsHint: "Principais perguntas, sentimento e temas dos comentários.",
    lockedBriefTitle: "Brief Criativo",
    lockedBriefHint: "Brief completo gerado por IA, adaptado à sua bible de marca.",
    lockedUnlock: "Cadastre-se grátis para desbloquear",
    lockedHint: "Crie uma conta gratuita para desbloquear os insights de comentários e o brief criativo.",
    cta: "Desbloquear análise completa",
    signUpToAnalyzeButton: "Cadastre-se para analisar →",
    themesLabel: "Temas",
    topQuestionsLabel: "Principais perguntas",
    sampleCommentsLabel: "Comentários",
    sentimentLabel: "Sentimento",
    creativeBriefLabel: "Brief Criativo",
    demoLabel: "Análise de exemplo — cole uma URL de Reel acima para analisar a sua",
    errorInvalidUrl: "Insira um link valido de Reel do Instagram.",
    errorMissingApi: "Configuração do endpoint de API ausente. Tente novamente mais tarde.",
    errorRateLimit: "Limite excedido. Cadastre-se para continuar.",
    errorNetwork: "Não foi possível acessar o Instagram. Tente novamente.",
  },
};

const formatCount = (count?: number) => {
  if (!count) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

const getScoreColor = (score: number | null | undefined) => {
  if (score == null) return "text-gray-400";
  if (score >= 8) return "text-green-600";
  if (score >= 5) return "text-amber-500";
  return "text-red-500";
};

const getScoreBg = (score: number | null | undefined) => {
  if (score == null) return "bg-gray-100 border-gray-200";
  if (score >= 8) return "bg-green-50 border-green-200";
  if (score >= 5) return "bg-amber-50 border-amber-200";
  return "bg-red-50 border-red-200";
};

const ANALYTICS_ENDPOINT = "/public/tools/instagram/reel/analytics-preview";

const DEMO_DATA: Extract<AnalyticsPreviewResponse, { status: "ok" }> = {
  status: "ok",
  video: {
    id: "DJkwuVxyNw7",
    caption: "To succeed you just need to do so many reps that it would be unreasonable for you to fail.",
    duration_sec: 45,
    thumbnail_url: "/preview/hormozi-thumbnail.webp",
    author: { username: "hormozi", display_name: "Alex Hormozi" },
    view_count: 7_746_198,
    like_count: 547_483,
  },
  preview: {
    teaser_summary:
      "One-minute success lesson: pick what you truly want, ignore the noise, and outwork everyone so success becomes inevitable.",
  },
  analytics: {
    hook_score: 8,
    hook_strategy: "High-stakes hypothetical prompt promising actionable advice",
    hook_text:
      "If all your books, videos, and tweets were erased from the internet forever and all you had was the next 60 seconds, what's the most useful thought you could leave to the world?",
    why_it_works:
      "It creates urgency and scarcity (60 seconds, everything erased) that forces the listener to prioritize and lean in, while promising a single, highly valuable takeaway. The contrast between total loss and a final chance hooks curiosity and emotional engagement immediately.",
    is_preview: false,
  },
  comment_insights: {
    themes: [
      "Consistency / volume (reps matter)",
      "Clarity of goals",
      "Ignoring others' opinions",
      "Praise for thoughtful delivery",
      "Positive reception of question format",
    ],
    sentiment: "positive",
    top_questions: [
      "What would you say?",
      "How do I figure out what I want?",
      "How do you ignore the opinions of others?",
      "What does 'do so much volume' look like in practice?",
    ],
    sample_comments: [
      "Instead of taking the 60 seconds to speak, he took 2/3 of his time to think. Masterful.",
      "1. Figure out what you want\n2. Ignore the opinions of others\n3. Do so much volume that it would be unreasonable that you would be unsuccessful",
      "That's the best response to this question. Volume matters ❤️",
    ],
  },
  creative_brief:
    '# Creative Brief — inspired by @hormozi\n\n**Must-have hook (verbatim):** "If all your books, videos, and tweets were erased from the internet forever and all you had was the next 60 seconds, what\'s the most useful thought you could leave to the world?"\n\n**Why this hook works:** High-stakes + scarcity forces the listener to lean in immediately. The contrast between total loss and a final chance creates emotional urgency.\n\n**Target audience:** Creators, micro-influencers, and marketers aged 18–45 who want faster growth and better analytics.\n\n**Primary message:** Clarity + volume beats perfection. Figure out what you want, ignore outside noise, and do so much volume that failure becomes unreasonable.\n\n**Recommended structure (30–45s):**\n- 0–3s: Deliver the hook verbatim with slow, intentional delivery\n- 3–30s: 3-step framework with pauses between each beat\n- 30–38s: Closing punchline + micro-CTA\n- 38–45s: Brand frame overlay',
};

const DEMO_URL = "https://www.instagram.com/reel/DJkwuVxyNw7/";

export default function InstagramAnalyticsPreview({ variant = "hero", demo = false }: { variant?: "section" | "hero"; demo?: boolean }) {
  const [url, setUrl] = useState(() => (demo ? DEMO_URL : ""));
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(() => (demo ? "success" : "idle"));
  const [response, setResponse] = useState<AnalyticsPreviewResponse | null>(() => (demo ? DEMO_DATA : null));
  const [isShowingDemo, setIsShowingDemo] = useState(demo);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const requestStartedAt = useRef<number | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const apiUrl = process.env.NEXT_PUBLIC_API_ENDPOINT as string;

  const locale = useMemo(() => (pathname ? getLocaleFromPathname(pathname) : DEFAULT_LOCALE), [pathname]);
  const copy = useMemo(() => localeCopy[locale] ?? localeCopy[DEFAULT_LOCALE], [locale]);
  const signupPath = useMemo(() => withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), [locale]);
  const isHero = variant === "hero";

  useEffect(() => {
    if (status !== "loading") {
      setAnalysisProgress(0);
      return;
    }
    setAnalysisProgress(0);
    const interval = window.setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 95) return prev;
        return Math.min(prev + Math.floor(Math.random() * 4) + 1, 95);
      });
    }, 160);
    return () => window.clearInterval(interval);
  }, [status]);

  const handleSubmit = async () => {
    // If the demo result is already showing and the URL hasn't changed, nothing to do
    if (url === DEMO_URL && isShowingDemo) return;
    if (isRateLimited) {
      trackPremiumFeatureClicked("unlock_analytics_preview");
      trackAnalyticsApiEvent("instagram_analytics_preview_click", { location: "rate_limit" });
      router.push(signupPath);
      return;
    }
    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl || !isValidSocialUrl(normalizedUrl, "instagram")) {
      setStatus("error");
      setIsRateLimited(false);
      setIsShowingDemo(false);
      setResponse({ status: "error", message: copy.errorInvalidUrl });
      return;
    }

    setStatus("loading");
    setResponse(null);
    setIsRateLimited(false);
    setIsShowingDemo(false);
    requestStartedAt.current = performance.now();

    if (!apiUrl) {
      setStatus("error");
      setResponse({ status: "error", message: copy.errorMissingApi });
      return;
    }

    const source = getSourceFromUrl(normalizedUrl);
    trackLinkPasted(source, { surface: "analytics_preview" });
    trackAnalyticsApiEvent("instagram_analytics_preview_requested", { source: "link", platform: "instagram" });
    const candidates = buildEndpointCandidates(ANALYTICS_ENDPOINT, ANALYTICS_ENDPOINT).map(e => `${apiUrl}${e}`);

    try {
      const result = await fetchWithFallback<AnalyticsPreviewResponse>(candidates, { url: normalizedUrl });

      if ("rateLimited" in result) {
        setStatus("error");
        setIsRateLimited(true);
        setPaywallFeature("unlock_analytics_preview");
        setResponse({ status: "error", message: copy.errorRateLimit });
        trackAnalyticsApiEvent("instagram_analytics_preview_failed", {
          platform: "instagram",
          reason: "rate_limited",
          error: "rate_limited",
        });
        return;
      }

      const { res, data } = result;
      if (res.ok && data.status === "ok") {
        setResponse(data);
        setStatus("success");
        const elapsedMs = requestStartedAt.current ? Math.round(performance.now() - requestStartedAt.current) : undefined;
        trackAnalyticsApiEvent("instagram_analytics_preview_loaded", {
          platform: "instagram",
          hook_score: data.analytics.hook_score,
          elapsed_ms: elapsedMs,
        });
        return;
      }

      setStatus("error");
      setResponse(data ?? { status: "error", message: copy.errorNetwork });
      const reason = classifyPreviewFailureReason({
        status: res.status,
        message: data.status === "error" ? data.message : null,
        reason: data.status === "error" ? data.reason : null,
      });
      trackAnalyticsApiEvent("instagram_analytics_preview_failed", { platform: "instagram", reason, error: "api_error" });
    } catch {
      setStatus("error");
      setResponse({ status: "error", message: copy.errorNetwork });
      trackAnalyticsApiEvent("instagram_analytics_preview_failed", {
        platform: "instagram",
        reason: "timeout",
        error: "network_error",
      });
    }
  };

  return (
    <>
      {paywallFeature && (
        <PaywallTracker trigger="premium_feature_clicked" feature={paywallFeature} onTracked={() => setPaywallFeature(null)} />
      )}
      <section className={isHero ? "w-full max-w-xl" : "w-full max-w-4xl mx-auto px-4 py-10"}>
        <div
          className={["border border-gray-200 rounded-3xl", isHero ? "bg-white/95 shadow-md p-5" : "bg-white shadow-lg p-6 md:p-10"].join(
            " "
          )}
        >
          <div className="flex flex-col gap-4">
            <div>
              <h2 className={isHero ? "text-xl font-semibold" : "text-2xl font-semibold"}>{copy.title}</h2>
              <p className="text-gray-600 mt-2">{copy.subtitle}</p>
            </div>
            <div className="flex flex-col md:flex-row gap-3 items-stretch">
              <Input
                value={url}
                onChange={event => setUrl(event.target.value)}
                onPaste={event => {
                  if (event.clipboardData?.getData("text")) {
                    trackAnalyticsApiEvent("instagram_analytics_link_pasted", { platform: "instagram" });
                  }
                }}
                onKeyDown={event => {
                  if (event.key === "Enter") handleSubmit();
                }}
                placeholder={copy.placeholder}
                aria-label="Instagram Reel URL"
                aria-invalid={status === "error"}
                className="h-11"
              />
              <Button onClick={handleSubmit} isLoading={status === "loading"} className="h-11 px-6">
                {status === "loading"
                  ? `${copy.analyzingButton} ${analysisProgress}%`
                  : isRateLimited
                    ? copy.rateLimitButton
                    : copy.analyzeButton}
              </Button>
            </div>
            {status === "error" && response?.status === "error" && <p className="text-sm text-destructive">{response.message}</p>}
          </div>

          {status === "success" && response?.status === "ok" && (
            <div className={isHero ? "mt-6 grid gap-5" : "mt-8 grid gap-6 md:grid-cols-[180px_1fr] items-start"}>
              <div className={isHero ? "contents" : "rounded-2xl overflow-hidden border border-gray-200 bg-gray-50"}>
                {!isHero &&
                  (response.video.thumbnail_url ? (
                    <Image
                      src={getProxiedImageSrc(response.video.thumbnail_url) ?? "/preview/hormozi-thumbnail.webp"}
                      alt={copy.thumbnailAlt}
                      width={360}
                      height={640}
                      sizes="180px"
                      className="w-full h-auto"
                      loading={isHero ? "eager" : "lazy"}
                      unoptimized
                    />
                  ) : (
                    <div className="w-full aspect-[9/16] bg-gradient-to-br from-pink-400 to-purple-600" />
                  ))}
              </div>

              <div className="flex flex-col gap-4">
                {/* Demo label */}
                {isShowingDemo && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    <span className="text-amber-500 text-sm">↑</span>
                    <span className="text-xs text-amber-700 font-medium">{copy.demoLabel}</span>
                  </div>
                )}

                {/* Video metadata — thumbnail inline for hero variant */}
                <div className="flex gap-3 items-center">
                  {isHero && (
                    <div className="rounded-xl overflow-hidden border border-gray-200 shrink-0 w-16 h-24">
                      {response.video.thumbnail_url ? (
                        <Image
                          src={getProxiedImageSrc(response.video.thumbnail_url) ?? "/preview/hormozi-thumbnail.webp"}
                          alt={copy.thumbnailAlt}
                          width={64}
                          height={96}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          unoptimized
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-600" />
                      )}
                    </div>
                  )}
                  <div>
                    <div className="flex flex-wrap gap-2 items-center text-sm text-gray-600">
                      <span className="font-medium text-gray-900">@{response.video.author.username}</span>
                      <span>•</span>
                      <span>{response.video.author.display_name}</span>
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600 mt-1">
                      <span>
                        {formatCount(response.video.view_count)} {copy.viewsLabel}
                      </span>
                      <span>
                        {formatCount(response.video.like_count)} {copy.likesLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Hook score + strategy */}
                <div className={`flex items-center gap-4 border rounded-2xl p-4 ${getScoreBg(response.analytics.hook_score)}`}>
                  <div className="flex flex-col items-center justify-center min-w-[64px]">
                    <span className={`text-3xl font-bold tabular-nums ${getScoreColor(response.analytics.hook_score)}`}>
                      {response.analytics.hook_score ?? "—"}
                    </span>
                    <span className="text-xs text-gray-500 mt-0.5">/10</span>
                    <span className="text-xs uppercase tracking-widest text-gray-400 mt-1">{copy.hookScoreLabel}</span>
                  </div>
                  {response.analytics.hook_strategy && (
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.hookStrategyLabel}</p>
                      <p className="mt-1 text-gray-800 font-medium text-sm">{response.analytics.hook_strategy}</p>
                    </div>
                  )}
                </div>

                {/* Revealed: hook text + why it works */}
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-4">
                  {response.analytics.hook_text && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.hookTextLabel}</p>
                      <p className="mt-2 text-gray-800 whitespace-pre-wrap">{response.analytics.hook_text}</p>
                    </div>
                  )}
                  {response.analytics.why_it_works && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.whyItWorksLabel}</p>
                      <div className="relative mt-2">
                        <p className="text-gray-800 whitespace-pre-wrap">{response.analytics.why_it_works}</p>
                        {!isShowingDemo && (
                          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-50 to-transparent" />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Comment insights — shown in demo, locked otherwise */}
                {isShowingDemo && response.comment_insights ? (
                  <div className="border border-gray-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.lockedInsightsTitle}</p>
                      <span className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 capitalize">
                        {response.comment_insights.sentiment}
                      </span>
                    </div>
                    <div className="flex gap-1.5 flex-wrap">
                      {response.comment_insights.themes.map(t => (
                        <span key={t} className="text-xs bg-gray-100 rounded-full px-2.5 py-1 text-gray-600">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-gray-400">{copy.topQuestionsLabel}</p>
                      {response.comment_insights.top_questions.slice(0, 3).map(q => (
                        <p key={q} className="text-sm text-gray-700">
                          · {q}
                        </p>
                      ))}
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-widest text-gray-400">{copy.sampleCommentsLabel}</p>
                      {response.comment_insights.sample_comments.slice(0, 2).map((c, i) => (
                        <p key={i} className="text-xs text-gray-600 bg-gray-50 rounded-xl px-3 py-2 whitespace-pre-wrap">
                          &ldquo;{c}&rdquo;
                        </p>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="relative border border-gray-200 rounded-2xl p-4 overflow-hidden">
                    <div className="blur-sm select-none pointer-events-none space-y-1" aria-hidden>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.lockedInsightsTitle}</p>
                      <p className="text-sm text-gray-600">{copy.lockedInsightsHint}</p>
                      <div className="flex gap-2 flex-wrap mt-2">
                        {["Why does this work?", "How often do you post?", "What app do you use?"].map(q => (
                          <span key={q} className="text-xs bg-gray-100 rounded-full px-3 py-1 text-gray-500">
                            {q}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/80 rounded-2xl">
                      <PiLockBold className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-600">{copy.lockedInsightsTitle}</span>
                    </div>
                  </div>
                )}

                {/* Creative brief — shown in demo (faded), locked otherwise */}
                {isShowingDemo && response.creative_brief ? (
                  <div className="relative border border-gray-200 rounded-2xl p-4 overflow-hidden">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">{copy.creativeBriefLabel}</p>
                    <p className="text-xs text-gray-600 font-mono whitespace-pre-wrap leading-relaxed">
                      {response.creative_brief.slice(0, 420)}
                    </p>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent rounded-b-2xl" />
                  </div>
                ) : (
                  <div className="relative border border-gray-200 rounded-2xl p-4 overflow-hidden">
                    <div className="blur-sm select-none pointer-events-none space-y-1" aria-hidden>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.lockedBriefTitle}</p>
                      <p className="text-sm text-gray-600">{copy.lockedBriefHint}</p>
                      <div className="mt-2 space-y-1">
                        {["## Hook Options", "**Option 1:** ...", "**Target audience:** ..."].map((line, i) => (
                          <p key={i} className="text-xs text-gray-400 font-mono">
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/80 rounded-2xl">
                      <PiLockBold className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-600">{copy.lockedBriefTitle}</span>
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">{isShowingDemo ? copy.demoLabel : copy.lockedHint}</p>
                  <Button
                    className="px-6 shrink-0"
                    onClick={() => {
                      trackPremiumFeatureClicked("unlock_analytics_preview");
                      trackAnalyticsApiEvent("instagram_analytics_preview_click", { location: "preview_cta" });
                      router.push(signupPath);
                    }}
                  >
                    {copy.cta}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
